import { useState, useEffect, useContext } from "react";

// Import your services
import quoteManagementService from "../../../services/QuoteManaementService";
import shlokaManagementService from "../../../services/ShlokaManagementService";
import topicManagementService from "../../../services/TopicManagementService";
import podcastManagementService from "../../../services/PodcastManagementService";
import informationManagementService from "../../../services/InformationManagementService";
import newsManagementService from "../../../services/DevotionalNewsManagement";
import ebooksSectionManagement from "../../../services/EBooksSectionManagement";
import testimonialManagementService from "../../../services/TestimonialManagementService";

// Auth Hook
import { AppGlobalContext } from "../../../contexts/AppContext.context";
import bhaktiSangeetManagementService from "../../../services/BhaktiSangeetManagement";
import magzineManagementService from "../../../services/MagzineManagementService";

// configs
import { CONTENT_STATUS_CONFIG } from "../../../config/App.config";

const useDashboardData = () => {
  // Auth State
  const { authUserDetails } = useContext(AppGlobalContext);

  // Centralized state for all dashboard data
  const [dashboardData, setDashboardData] = useState({
    quote: null,
    shloka: null,
    magzine: null,
    topics: [],
    podcasts: [],
    bhaktiSangeet: [],
    information: [],
    devotionalNews: [],
    ebooks: [],
    testimonials: [],
  });

  // Loading states for each API
  const [loading, setLoading] = useState({
    quote: false,
    shloka: false,
    magzine: false,
    topics: false,
    podcasts: false,
    bhaktiSangeet: false,
    information: false,
    devotionalNews: false,
    ebooks: false,
    testimonials: false,
    global: true,
  });

  // Error states for each API
  const [errors, setErrors] = useState({
    quote: null,
    shloka: null,
    magzine: null,
    topics: null,
    podcasts: null,
    bhaktiSangeet: null,
    information: null,
    devotionalNews: null,
    ebooks: null,
    testimonials: null,
  });

  // Shared defaults
  const COMMON = {
    page: 1,
    limit: 10,
    isActive: true,
    status: CONTENT_STATUS_CONFIG.PUBLISHED,
    language: "English",
    sort: "desc",
    sortBy: "createdAt",
  };

  // All section configs in one place
  const API_CONFIG = {
    quote: {
      service: quoteManagementService,
      params: (c) => ({ language: c.language }),
    },
    shloka: {
      service: shlokaManagementService,
      params: (c) => ({ language: c.language }),
    },
    magzine: {
      service: magzineManagementService,
      params: (c) => ({ 
        page: c.page,
        limit: 2,
        isActive: c.isActive,
        status: c.status,
        language: c.language,
      }),
    },
    topics: {
      service: topicManagementService,
      params: (c) => ({
        page: c.page,
        limit: c.limit,
        isActive: c.isActive,
        status: c.status,
        language: c.language,
      }),
    },
    podcasts: {
      service: podcastManagementService,
      params: (c) => ({
        page: c.page,
        limit: c.limit,
        isActive: c.isActive,
        status: c.status,
        language: c.language,
      }),
    },
    bhaktiSangeet: {
      service: bhaktiSangeetManagementService,
      params: (c) => ({
        page: c.page,
        limit: c.limit,
        isActive: c.isActive,
        status: c.status,
        language: c.language,
      }),
    },
    information: {
      service: informationManagementService,
      params: (c) => ({
        page: c.page,
        limit: 2,
        isActive: c.isActive,
        status: c.status,
        language: c.language,
      }),
    },
    devotionalNews: {
      service: newsManagementService,
      params: (c) => ({
        page: c.page,
        limit: 2,
        isActive: c.isActive,
        status: c.status,
        language: c.language,
      }),
    },
    ebooks: {
      service: ebooksSectionManagement,
      params: (c) => ({
        page: c.page,
        limit: c.limit,
        isActive: c.isActive,
        status: c.status,
        language: c.language,
      }),
    },
    testimonials: {
      service: testimonialManagementService,
      params: (c) => ({
        page: c.page,
        limit: c.limit,
        isActive: c.isActive,
        status: c.status,
        language: c.language,
      }),
    },
  };

  // --------- State update helpers ---------
  // Set loading state
  const setLoad = (k, v) => setLoading((p) => ({ ...p, [k]: v }));

  // Set error state
  const setErr = (k, e) => setErrors((p) => ({ ...p, [k]: e }));

  // Set data state
  const setData = (k, d) => setDashboardData((p) => ({ ...p, [k]: d }));

  // Fetch data helper function
  const fetchData = async (key, service, params) => {
    try {
      setLoad(key, true);
      setErr(key, null);
      const res = await service.getAll(params);
      const data = res?.data ?? res;
      setData(key, data);
      return data;
    } catch (e) {
      setErr(key, e?.message || `Failed to load ${key}`);
      return null;
    } finally {
      setLoad(key, false);
    }
  };

  // Fetch all dashboard data
  const fetchAllDashboardData = async () => {
    try {
      setLoad("global", true);
      const cfg = Object.entries(API_CONFIG);
      await Promise.allSettled(
        cfg.map(([key, c]) => fetchData(key, c.service, c.params(COMMON)))
      );
    } finally {
      setLoad("global", false);
    }
  };

  // Refresh specific data
  const refreshData = async (key) => {
    const c = API_CONFIG[key];
    if (!c) return null;
    return fetchData(key, c.service, c.params(COMMON));
  };

  // Fetch data on component mount
  useEffect(() => {
    if (!authUserDetails?.uid) {
      return;
    } else {
      COMMON.language = authUserDetails?.preferredLanguage || "English";
      fetchAllDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUserDetails]);

  // Create slides for AutoCarousel
  const carouselSlides = [];
  if (Array.isArray(dashboardData.magzine) && dashboardData.magzine.length) {
    dashboardData.magzine.forEach((m) => {
      carouselSlides.push({
        id: m.id,
        type: "magzine",
        title: m.title,
        description: m.description,
        imageUrl: m.thumbnailImage,
        raw: m,
      });
    });
  }
  if (dashboardData.shloka) {
    carouselSlides.push({
      id: dashboardData.shloka.id,
      type: "shloka",
      text: dashboardData.shloka.text || dashboardData.shloka.shlokaText,
      meaning: dashboardData.shloka.meaning || dashboardData.shloka.shlokaMeaning,
      imageUrl: require("../../../assets/images/Shloka-BG.png"),
    });
  }
  if (dashboardData.quote) {
    carouselSlides.push({
      id: dashboardData.quote.id,
      type: "quote",
      text: dashboardData.quote.text,
      author: dashboardData.quote.source,
      imageUrl: require("../../../assets/images/Quote-BG.png"),
    });
  }

  return {
    data: dashboardData,
    loading,
    errors,
    isAnyLoading: Object.values(loading).some(Boolean),
    carouselSlides,
    fetchAllDashboardData,
    refreshData,
    refreshQuote: () => refreshData("quote"),
    refreshShloka: () => refreshData("shloka"),
    refreshMagzine: () => refreshData("magzine"),
    refreshTopics: () => refreshData("topics"),
    refreshPodcasts: () => refreshData("podcasts"),
    refreshBhaktiSangeet: () => refreshData("bhaktiSangeet"),
    refreshInformation: () => refreshData("information"),
    refreshDevotionalNews: () => refreshData("devotionalNews"),
    refreshEbooks: () => refreshData("ebooks"),
    refreshTestimonials: () => refreshData("testimonials"),
  };
};

export default useDashboardData;
